import { useEffect, useState } from 'react';
import { useSharedServiceState } from '@getvim/internal-vim-os-sdk/react';
import { PatientSourceVaultToken } from '@getvim/patient-lookup-service/types';
import { PatientWithPII } from '@getvim/internal-vim-os-sdk/types';
import { findInsurerEnum } from '@getvim-core-apps/commons';
import { Insurer } from '@getvim-os/standard';
import { createLogger } from '../utils/createLogger';
import { checkPatientEligibility } from '../utils/checkPatientEligibility';
import { AppPatient, Tab } from '../types';
import { useAnalytics } from '../analytics/AnalyticsProvider';

const logger = createLogger('[usePatientLookupSharedService]');

const getSourceVaultTokenAndInsurer = (
  patientLookupResponse:
    | {
        patientSourceVaultTokens: PatientSourceVaultToken[];
        patientToken: string;
      }
    | undefined,
): {
  patientSourceVaultToken: string | undefined;
  insurer: Insurer | undefined;
} => {
  if (!patientLookupResponse?.patientSourceVaultTokens?.length) {
    return {
      patientSourceVaultToken: undefined,
      insurer: undefined,
    };
  }
  const patientSourceVaultToken = patientLookupResponse?.patientSourceVaultTokens.find(
    ({ isInsurer }) => isInsurer,
  );

  return {
    patientSourceVaultToken: patientSourceVaultToken?.sourceVaultToken,
    insurer: findInsurerEnum(patientSourceVaultToken?.source),
  };
};

export const usePatientLookupSharedService = (
  ehrPatient: PatientWithPII,
): [AppPatient | undefined, boolean] => {
  const [patient, setPatient] = useState<AppPatient>();
  const [isPatientEligible, setIsPatientEligible] = useState<boolean>(false);
  const serviceState = useSharedServiceState();
  const [patientLookupResponse, setPatientLookupResponse] = useState<{
    patientSourceVaultTokens: PatientSourceVaultToken[];
    patientToken: string;
  }>();
  const { analyticsClient } = useAnalytics();
  useEffect(() => {
    const { 'patient-lookup-service': patientLookupService } = serviceState;

    if (!patientLookupService) {
      return;
    }

    // Extract the response payload from the service
    const { patientLookupResponse: currentResponse, serviceInit } =
      patientLookupService.customMessage.payload;

    if (serviceInit) {
      logger.info('Received patient-lookup-service init event', {
        instanceId: serviceInit.instanceId,
        noPHI: true,
      });
      return;
    }

    // Skip processing if the token hasn't changed
    if (patientLookupResponse?.patientToken === currentResponse?.patientToken) {
      return;
    }
    logger.info('Set new lookup service response', {
      noPHI: true,
    });
    setPatientLookupResponse(currentResponse);
  }, [serviceState, ehrPatient, patientLookupResponse]);

  useEffect(() => {
    if (!ehrPatient && !!patient) {
      setIsPatientEligible(false);
      analyticsClient.setContext({ patient: undefined }, Tab.PriorAuthInquiry);
      setPatient(undefined);
    }

    if (!!ehrPatient && patientLookupResponse?.patientToken === ehrPatient?.token) {
      const { patientSourceVaultToken, insurer } =
        getSourceVaultTokenAndInsurer(patientLookupResponse);
      const { insurance, ...restPatient } = ehrPatient;
      const patientWithInsurer: AppPatient = {
        ...restPatient,
        insurance: {
          ...insurance,
          insurer,
        },
        patientSourceVaultToken,
      };

      // Check patient eligibility
      const isEligible = checkPatientEligibility(patientWithInsurer);
      logger.info('Patient Lookup Response and eligibility check', {
        isEligible,
        insurer: patientWithInsurer?.insurance?.insurer,
        ehrInsurance: patientWithInsurer?.insurance?.ehrInsurance,
        vimPatientId: patientWithInsurer?.identifiers?.vimPatientId,
        patientSourceVaultTokens: !!patientWithInsurer?.patientSourceVaultToken,
        noPHI: true,
      });
      // set default tab here
      analyticsClient.setContext({ patient: patientWithInsurer }, Tab.PriorAuthInquiry);
      setPatient(patientWithInsurer);
      setIsPatientEligible(isEligible);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientLookupResponse, ehrPatient]);

  return [patient, isPatientEligible];
};
